import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { memberApi } from "@/lib/memberAuth";
import { toast } from "sonner";
import { Storefront, ArrowSquareOut } from "@phosphor-icons/react";

export default function MemberVendors() {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    memberApi.get("/portal/vendors")
      .then(({ data }) => setVendors(data.vendors || []))
      .catch(() => toast.error("Could not load vendors"))
      .finally(() => setLoading(false));
  }, []);

  const grouped = vendors.reduce((acc, v) => {
    const cat = v.category || "Other";
    (acc[cat] = acc[cat] || []).push(v);
    return acc;
  }, {});

  return (
    <div data-testid="portal-vendors">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline mb-1">Portal</div>
          <h1 className="font-display text-3xl font-bold text-navy tracking-tight flex items-center gap-2">
            <Storefront size={24} weight="duotone" className="text-ember" /> Associated Vendors
          </h1>
          <p className="text-sm text-navy/70 mt-2 max-w-2xl">
            Service providers offering logistics-adjacent tools — TMS, CRM, freight AI, insurance, fintech. Vendors
            apply and pay to be listed; EPAS curates the shortlist.
          </p>
        </div>
        <Link to="/vendors/apply" data-testid="vendors-apply-btn"
          className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest bg-navy text-white px-3 py-2 hover:bg-navy/90">
          Apply to list
        </Link>
      </header>

      {loading ? (
        <div className="border border-black/10 bg-white p-8 text-center text-navy/50 text-sm">Loading…</div>
      ) : vendors.length === 0 ? (
        <div className="border border-black/10 bg-white p-8 text-center text-navy/50 text-sm">
          No listed vendors yet. Category is still curated — quality over volume.
        </div>
      ) : (
        Object.entries(grouped).map(([cat, list]) => (
          <section key={cat} className="mb-8" data-testid={`vendors-cat-${cat}`}>
            <div className="overline mb-3">{cat}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {list.map((v) => (
                <div key={v.id} className="border border-black/10 bg-white p-5 flex flex-col" data-testid={`vendor-${v.id}`}>
                  <div className="flex items-start gap-3">
                    {v.logo_url ? (
                      <img src={v.logo_url} alt="" className="w-12 h-12 object-contain border border-black/5 bg-white" />
                    ) : (
                      <div className="w-12 h-12 bg-navy/5 flex items-center justify-center text-navy/40 text-xs font-bold">{v.company_name.slice(0, 2).toUpperCase()}</div>
                    )}
                    <div className="min-w-0">
                      <div className="font-display font-bold text-navy truncate">{v.company_name}</div>
                      <div className="text-xs text-navy/50 uppercase tracking-widest">{v.country}</div>
                    </div>
                  </div>
                  {v.pitch && <p className="text-sm text-navy/70 mt-3 flex-1">{v.pitch}</p>}
                  {v.website && (
                    <a href={v.website} target="_blank" rel="noopener noreferrer" data-testid={`vendor-${v.id}-website`}
                       className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-ember hover:text-navy mt-3">
                      Visit <ArrowSquareOut size={11} />
                    </a>
                  )}
                </div>
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}
